import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from "react";
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { AddToPhotos } from '@mui/icons-material';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import AudioRecorder from "features/AudeoRecorderNew";
import COLOR from "utils/constants/color";
import Loading from "components/Loading";
import ModalMenu from "components/Modals";
import TextEditor from "components/TextEditor";
import styled from "styled-components";
import { dropDownMenu } from "utils/config/reference";
import { imageUploader } from "utils/methods/imageUploader";
import { UPLOAD_IMAGE_TO_FIREBASE } from "utils/constants/enum"
import SentryWrap from 'components/Sentry';
import { useCurrentUser } from 'store/users/selectors';
// MUI v5 styled components
const StyledFormControl = muiStyled(FormControl)(({ theme }) => ({
  minWidth: 120,
  width: '100%',
}));

const StyledCheckbox = muiStyled(Checkbox)(({ theme }) => ({
  '&.Mui-checked': {
    color: COLOR.green100,
  },
}));

const StyledFormGroup = muiStyled(FormGroup)(({ theme }) => ({
  flexDirection: 'row',
  gap: '12px',
}));

const StyledInputLabel = muiStyled(InputLabel)({
  backgroundColor: 'white',
  paddingRight: 5,
});

// Styled-components for layout
const AssignmentDescription = styled.span`
  color: ${COLOR.black300};
  display: block;
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
`;

const Detail = styled(AssignmentDescription)`
  color: ${COLOR.black600};
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-top: ${(p) => (p.index == 0 ? "0px" : "24px")};
`;

const Text = styled.div`
  font-size: 12px;
`;

const Wrapper = styled.div`
  background: #ececec;
  min-height: 100vh;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const StackedCollage = styled.div`
  position: relative;
  width: 100%;
  max-width: 98%;
  min-height: auto;
  padding: 10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

const Photo = styled.div`
  position: inherit !important;
  width: 140px;
  height: 200px;
  padding: 8px;
  background: white;
  border: 1px solid #ddd;
  border-top: 1px solid rgb(204, 204, 204) !important;
  border-radius: 6px;
  box-shadow: 0px 8px 18px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease, z-index 0.3s ease;
  cursor: pointer;
  transform: rotate(${(props) => props.rotation}deg) !important;
  z-index: ${(props) => props.zIndex};

  &:hover {
    z-index: 999999 !important;
    transform: scale(1.05) rotate(0deg) !important;
  }

  @media (max-width: 1200px) {
    width: 120px;
  }

  @media (max-width: 768px) {
    width: 100px;
  }

  @media (max-width: 480px) {
    width: 80px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 4px;
  object-fit: cover;
`;

const ImagePlaceHolder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 10;
`;

const LoadingText = styled.span`
  margin-left: 10px;
  font-size: 14px;
  color: black;
`;

const ChangeImageOverlay = styled.div`
    position: absolute;
    top: 0px;
    bottom: 0;
    left: 0;
    right: 0;
    text-align: center;
    background: rgba(0, 0, 0, 0.4);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
`;

const getLayout = (index) => {
  const columns = 4;
  const row = Math.floor(index / columns);
  const col = index % columns;

  const top = `${row * 5 + Math.random() * 10}%`;
  const left = `${col * 20 + Math.random() * 5}%`;
  const rotate = Math.random() * 10 - 5;

  return { top, left, rotate };
};

const PhotoCollageView = ({
  index,
  question = {},
  selectData = true,
  isPreview = false,
  frameCollage = [],
  handlerOnChangeData,
  handleOnChangeUploader,
  collageLength = 6,
  gradePreview = false,
  showFullPreview = false,
  from = "",
  showResponsePreview=true
}) => {
  const [selectedCount, setSelectedCount] = useState(6);
  const [collageFrames, setCollageFrames] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [loadingMap, setLoadingMap] = useState(0);
  const [loader, setLoader] = useState({ frameId: null, value: false });
  const [imageModal, setImageModal] = useState(false);
  const showAlert = useAlert();
  const currentUser = useCurrentUser();
  const isStudentShareeAssignment = from === "shareAssignment" && currentUser?.role === "student";

  useEffect(() => {
    setSelectedCount(collageLength);
  }, [collageLength]);

  useEffect(() => {
    if (frameCollage.length > 0) {
      setCollageFrames(frameCollage);
    }
  }, [frameCollage]);

  useEffect(() => {
    if (frameCollage.length > 0) {
      return;
    }
    // const filteredFrames = photoCollageData.slice(0, selectedCount);
    // setCollageFrames(filteredFrames);
    // handlerOnChangeData(collageFrames, "collageFrame");
  }, [selectedCount]);

  const handleImageUpload = (e, frameId) => {
    if (loader?.value) return;
    const file = e.target.files[0];
    if (!file) return;

    const maxSizeInMB = 5;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      e.target.value = null;
      showAlert(`File size should not exceed ${maxSizeInMB} MB.`, 'error');
      return;
    }

    // Set loader state immediately when upload starts
    setLoader({ frameId, value: true });

    const reader = new FileReader();
    reader.onload = (event) => {
      setCollageFrames((prevFrames) =>
        prevFrames.map((frame) =>
          String(frame?.id) === String(frameId)
            ? { ...frame, image: event.target.result }
            : frame
        )
      );
    };
    reader.readAsDataURL(file);
    uploadToStorage(file, frameId);
  };

  const onProgress = (per, frameId) => {
    setLoadingMap(per);
    setLoader({ frameId, value: true });
  };


  const uploadToStorage = async (file, frameId) => {
    try {
      const download_url = await imageUploader(file, (progress) =>
        onProgress(progress, frameId),UPLOAD_IMAGE_TO_FIREBASE
      );
      if (download_url) {
        await handleOnChangeUploader(download_url, null, frameId,collageFrames,index);
      } else {
        showAlert("Unable to upload image.", 'error');
        setCollageFrames((prevFrames) =>
          prevFrames.map((frame) =>
            String(frame?.id) === String(frameId) ? { ...frame, image: null } : frame
          )
        );
      }
      setLoader({ frameId, value: false });
    } catch (error) {
      console.log(error);
      Sentry.captureException(`607 Error in uploadToStorage: ${error}`);
      showAlert("Error uploading image. Please try again.", 'error');
      setCollageFrames((prevFrames) =>
        prevFrames.map((frame) =>
          String(frame?.id) === String(frameId) ? { ...frame, image: null } : frame
        )
      );
      setLoader({ frameId, value: false });
    }
  };

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    let updatedValues = [...(question.categoryType || [])];

    if (checked) {
      updatedValues.push(value);
    } else {
      updatedValues = updatedValues.filter((item) => item !== value);
    }

    handlerOnChangeData(updatedValues, "categoryType");
  };
  const getRotation = (index) => {
    const angles = [-6, 5, -3, 9, 3, 10];
    return angles[index % angles.length];
  };

  return (
    <SentryWrap>
    <div className="collage-builder">
      {selectData && (
        <div className="collage-controls">
          {!isStudentShareeAssignment &&  from !== "shareAssignment" && (
          <StyledFormControl>
            <StyledInputLabel id="type">
              Select Collage Count
            </StyledInputLabel>
            <Select
              labelId="type"
              id="type"
              value={question?.collageLength}
              required
              onChange={(v) => {
                handlerOnChangeData(v.target.value, "collageLength");
              }}
              label="Select Collage Count"
              disabled={isStudentShareeAssignment}
            >
              {dropDownMenu?.map((v) => (
                <MenuItem key={v.value} value={v.value}>
                  {v.value}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
          )}

          {!isStudentShareeAssignment &&  from !== "shareAssignment" && (
                      <div className="category-type-radio-type">
                      <Detail className="mb-10px">Answer Type</Detail>
                      <StyledFormGroup>
                        <FormControlLabel
                          control={
                            <StyledCheckbox checked={true} disabled={true} value="Collage" />
                          }
                          label="Collage"
                          style={{ marginRight: "10px" }}
                        />
          
                        <FormControlLabel
                          control={
                            <StyledCheckbox
                              checked={question.categoryType?.includes("Text")}
                              onChange={handleCheckboxChange}
                              value="Text"
                            />
                          }
                          label="Collage With Description"
                          style={{ marginRight: "10px" }}
                        />
          
                        <FormControlLabel
                          control={
                            <StyledCheckbox
                              checked={question.categoryType?.includes("Audio")}
                              onChange={handleCheckboxChange}
                              value="Audio"
                            />
                          }
                          label="Collage With Audio"
                          style={{ marginRight: "10px" }}
                        />
                      </StyledFormGroup>
                    </div>
          )}



{!isStudentShareeAssignment &&  from !== "shareAssignment" && (
          <div className="mt-10px">
            {(question?.categoryType?.includes("Text") ||
              question?.categoryType?.includes("Audio")) && (
              <TextField
                fullWidth
                id="collageWithText"
                name="collageWithText"
                label="Description"
                InputLabelProps={{ shrink: true, required: true }}
                type="text"
                required
                variant="outlined"
                onChange={(e) => {
                  handlerOnChangeData(e.target.value, "type_question_desc");
                }}
                value={question.type_question_desc}
                disabled={isStudentShareeAssignment}
              />
            )}
          </div>
          )}
        </div>
      )}

      {showFullPreview  && showResponsePreview && <div className="">Preview</div>}
    {showResponsePreview && <div
        className="collage-container"
        style={{ position: "relative", width: "100%", height: "auto" }}
      >
        <div
          className="photos-wrapper"
          style={{
            position: "relative",
            height: "367px",
            overflow: "auto",
            padding: "10px",
            marginTop: "20px",
            marginBottom: "20px",
          }}
        >
          <StackedCollage>
            {collageFrames?.map((frame, index) => {
              const layout = getLayout(frame?.id);
              const rotation = getRotation(index);
              return (
                <Photo
                  key={frame?.id}
                  rotation={rotation}
                  zIndex={(index % 6) + 1}
                  className={`photo-frameCstm photo-frame ${frame?.className} ${
                    hoveredId === frame?.id ? "hovered" : ""
                  }`}
                >
                  {loader?.frameId === frame.id && loader?.value && (
                    <LoadingOverlay>
                      <Loading />
                      <LoadingText>
                        {loadingMap ? `${Math.round(loadingMap)}%` : "Uploading..."}
                      </LoadingText>
                    </LoadingOverlay>
                  )}

                  {frame.image ? (
                    <>
                      <ModalMenu
                        type="imageModal"
                        image={frame.image}
                        open={imageModal}
                        close={() => setImageModal(false)}
                      />
                      <Image
                        src={frame.image}
                        alt={`Collage item ${frame.id}`}
                        className="cursor-pointer photoFrameCstm"
                        onClick={() => {
                          if (gradePreview) {
                            setImageModal(true);
                          }
                        }}
                      />
                    </>
                  ) : gradePreview ? (
                    <div className="upload-placeholder">Collage-{frame.id}</div>
                  ) : (
                    <ImagePlaceHolder>
                      <label
                        htmlFor={`upload-${question?.id || index}-${frame.id}`}
                        className="upload-label"
                        style={{
                          cursor: loader?.value ? "not-allowed" : "pointer",
                        }}
                      >
                        <div className="flex flex-col items-center">
                          <AddToPhotos color="primary" />
                          <Text>Upload Image</Text>
                        </div>
                      </label>
                      <input
                        id={`upload-${question?.id || index}-${frame.id}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, frame.id)}
                        className="file-input"
                        style={{ display: "none" }}
                        disabled={isPreview || loader?.value || (from === "shareAssignment" && currentUser?.role === "student")}
                      />
                    </ImagePlaceHolder>
                  )}

                  {!gradePreview && frame.image && (
                    <ChangeImageOverlay className='changeImageOverlayCstm'>
                      <label
                        htmlFor={`upload-${question?.id || index}-${frame.id}`}
                        style={{
                          cursor: loader?.value ? "not-allowed" : "pointer",
                        }}
                      >
                        Change Image
                      </label>
                      <input
                        id={`upload-${question?.id || index}-${frame.id}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, frame.id)}
                        className="file-input"
                        style={{ display: "none" }}
                        disabled={isPreview || loader?.value === true || (from === "shareAssignment" && currentUser?.role === "student")}
                      />
                    </ChangeImageOverlay>
                  )}
                </Photo>
              );
            })}
          </StackedCollage>
        </div>
      </div>}

      {showFullPreview && showResponsePreview && (
        <div className="flex flex-col gap-10px">
          {(question?.categoryType?.includes("Text") ||
            question?.categoryType?.includes("Audio")) && (
            <Detail>{question?.type_question_desc}</Detail>
          )}

          <>
            {question?.categoryType?.includes("Text") && (
              <div className="mt-10px">
                <TextEditor
                  handleChange={() => {}}
                  value={""}
                  autoFocus={false}
                  disabled={true}
                  containerClassName="question-ebook-text-field"
                />
              </div>
            )}
            {question?.categoryType?.includes("Audio") && (
              <div className="mt-10px">
                <AudioRecorder
                  maxTime={question.duration || 120}
                  handleAudio={() => {}}
                  index={index}
                  currentSubmission={{}}
                  refresh={() => {}}
                  isPreview
                />
              </div>
            )}
          </>
        </div>
      )}
    </div>
    </SentryWrap>
  );
};

export default PhotoCollageView;